import { Card, CardContent, CardHeader, CardTitle, Money } from '@mlm/ui';

/**
 * Shared stat tiles + sparkline for the creator portal. Lifted out of
 * `pages/dashboard.tsx`, which `course-stats.tsx` was already importing FROM —
 * a page importing a component from another page is how a "just move this file"
 * change breaks two screens at once.
 *
 * Deliberately dependency-free: `recharts` exists in the admin and member SPAs
 * at two different major versions, and a bar-per-day chart does not justify
 * pulling a third copy into a bundle that has none.
 */

export function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

/** A money stat, rendered through the shared `<Money>` so the currency, the
 *  sign and the tabular alignment match every other money surface. */
export function MoneyStat({
  label,
  amount,
  hint,
}: {
  label: string;
  amount: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">
          <Money amount={amount} direction="neutral" />
        </div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export type SparkPoint = { date: string; value: number; label?: string };

/** Tiny dependency-free bar sparkline over a generic {date, value} series. */
export function Sparkline({
  points,
  emptyText = 'No activity in the last 30 days.',
  ariaLabel = 'Daily activity',
}: {
  points: SparkPoint[];
  emptyText?: string;
  ariaLabel?: string;
}) {
  if (points.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }
  const max = Math.max(1, ...points.map((p) => p.value));
  return (
    <div className="flex h-24 items-end gap-1" aria-label={ariaLabel}>
      {points.map((p) => (
        <div
          key={p.date}
          className="flex flex-1 flex-col justify-end"
          title={p.label ?? `${p.date}: ${p.value}`}
        >
          <div
            className="w-full rounded-sm bg-primary/70"
            style={{ height: `${Math.max(2, (p.value / max) * 100)}%` }}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * The original views-per-day sparkline, kept as a named wrapper so the
 * dashboard and per-course stats pages read the same as before.
 */
export function ActivitySparkline({
  points,
}: {
  points: { date: string; views: number; completions: number }[];
}) {
  return (
    <Sparkline
      points={points.map((p) => ({
        date: p.date,
        value: p.views,
        label: `${p.date}: ${p.views} views, ${p.completions} completions`,
      }))}
      ariaLabel="Daily views"
    />
  );
}

// ── Learning-palette surfaces (the redesigned Dashboard / Courses) ──────────
// The shadcn `Card` above stays for the pages that still use it; these carry the
// academy look — a hairline border, a soft radius, no shadow — so a page reads
// as part of Creator Studio rather than a generic admin tool.

/** A plain card in the learning palette. */
export function LmsCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-2xl border border-lms-hairline bg-white ${className}`}>{children}</div>
  );
}

/**
 * A figure tile. `delta` is a chip under the value — it must be a STATEMENT of
 * what was counted ("+12 in 30 days", "48 reviews"), never a bare percentage
 * against a period nobody defined.
 */
export function LmsStat({
  label,
  value,
  delta,
  small,
}: {
  label: string;
  value: string;
  delta?: string;
  small?: boolean;
}) {
  if (small) {
    return (
      <div className="rounded-[14px] border border-lms-hairline bg-white px-[18px] py-4">
        <div className="text-[12.5px] font-semibold text-lms-muted">{label}</div>
        <div className="mt-1.5 font-lms-heading text-[22px] font-bold tabular-nums text-lms-ink">
          {value}
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-[128px] flex-col justify-between rounded-2xl border border-lms-hairline bg-white p-6">
      <div className="text-[13px] font-semibold text-lms-muted">{label}</div>
      <div>
        <div className="font-lms-heading text-[30px] font-bold tabular-nums text-lms-ink">
          {value}
        </div>
        {delta && (
          <div className="mt-1 inline-flex rounded-full bg-lms-light px-2.5 py-0.5 text-[11.5px] font-bold text-lms">
            {delta}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The 30-day views chart: one bar per day, the busiest day in gold.
 *
 * Takes an ALREADY zero-filled series (`zeroFillDays`) — the API omits days
 * with no activity, and a chart that silently drops them turns four busy days
 * into a month of solid traffic.
 */
export function ViewsChart({ points }: { points: { date: string; views: number }[] }) {
  if (points.length === 0) {
    return <p className="text-sm text-lms-muted">No activity in the last 30 days.</p>;
  }
  const max = Math.max(1, ...points.map((p) => p.views));
  return (
    <div className="flex h-[110px] items-end gap-[3px]" aria-label="Daily views">
      {points.map((p) => (
        <div
          key={p.date}
          className="flex h-full flex-1 flex-col justify-end"
          title={`${p.date}: ${p.views} view${p.views === 1 ? '' : 's'}`}
        >
          <div
            className={`w-full rounded-t-[3px] ${p.views === max && max > 0 ? 'bg-lms-gold' : 'bg-lms'}`}
            style={{ height: `${Math.max(3, (p.views / max) * 100)}%` }}
          />
        </div>
      ))}
    </div>
  );
}
